import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListStorageStocksQuery } from '../../../redux/api/stockApi';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useListBackstageLocationsQuery,
  useListCrewLocationsQuery,
} from '../../../redux/api/locationApi';
import { useCreateCrewDeliveryMutation } from '../../../redux/api/crewDeliveryApi';
import { useGetContingentStatusQuery } from '../../../redux/api/contingentApi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useApiErrorToast } from '../../../hooks/useApiError';
import ContingentExceededModal from '../../../components/delivery/ContingentExceededModal';
import DestinationPicker from './DestinationPicker';
import DeliveryDestinationCard from './DeliveryDestinationCard';
import StockSummaryPanel from './StockSummaryPanel';
import TemplatePanel from './TemplatePanel';

const CreateCrewDelivery = () => {
  const authState = useSelector((state) => state.auth);
  const hasMainStorage = !!authState?.currentEvent?.main_storage;

  const {
    data: stocksData,
    isFetching: stocksFetching,
    refetch: refetchStocks,
  } = useListStorageStocksQuery(
    authState?.currentEvent?.main_storage ?? skipToken,
    {
      // Refetch when the component mounts or when args change.
      refetchOnMountOrArgChange: true,
      skip: !hasMainStorage,
    }
  );

  // Only offer articles that are approved for crew/backstage delivery
  // (Article.crew) AND actually have stock at the main storage — a delivery
  // position can't be created without available stock. The create endpoint
  // rejects non-crew articles as well; this keeps them out of the picker.
  const stocks =
    hasMainStorage && stocksData
      ? stocksData.filter((s) => s.crew && s.quantity_available > 0)
      : null;

  // Destinations are scoped to the selected event server-side, so another
  // event's crew / backstage stops are never returned here.
  const eventId = authState?.currentEvent?.id;
  const { data: crewLocations } = useListCrewLocationsQuery(eventId, {
    skip: !eventId,
  });
  const { data: backstageLocations } = useListBackstageLocationsQuery(eventId, {
    skip: !eventId,
  });
  const [createDelivery, { isLoading }] = useCreateCrewDeliveryMutation();

  const navigate = useNavigate();

  // Each line holds only the articles explicitly added to that destination:
  // { destination, destinationName, destinationType, articles: [{ stock, quantity }] }
  const [deliveryLines, setDeliveryLines] = useState([]);
  const [comment, setComment] = useState('');
  const [noteComment, setNoteComment] = useState('');
  const [contingentError, setContingentError] = useState(null);
  const apiErrorToast = useApiErrorToast();

  const destinationIds = deliveryLines
    .map((l) => l.destination)
    .filter(Boolean);
  const { data: contingentStatus } = useGetContingentStatusQuery(
    { eventId: authState?.currentEvent?.id, destinationIds },
    { skip: !authState?.currentEvent?.id || destinationIds.length === 0 }
  );
  const contingentLookup = useMemo(() => {
    const map = {};
    (contingentStatus?.results || []).forEach((row) => {
      map[`${row.destination_id}|${row.article_id}`] = row;
    });
    return map;
  }, [contingentStatus]);

  // Force refetch stocks when currentEvent changes.
  useEffect(() => {
    if (hasMainStorage) {
      refetchStocks();
    }
  }, [authState?.currentEvent?.id, refetchStocks, hasMainStorage]);

  // Clear delivery lines when event changes to avoid stale data.
  useEffect(() => {
    setDeliveryLines([]);
    setComment('');
    setNoteComment('');
  }, [authState?.currentEvent?.id]);

  // Per-article totals across all destinations; drives the stock summary
  // and the overbooked flag.
  const stockDetails = useMemo(() => {
    return (stocks ?? []).map((stock) => {
      const stockOnThisDelivery = deliveryLines.reduce((sum, line) => {
        const article = line.articles.find((a) => a.stock.id === stock.id);
        return sum + (article ? article.quantity : 0);
      }, 0);
      return {
        stockId: stock.id,
        stockArticleName: stock.article_name,
        stockAvailable: stock.quantity_available,
        stockOnThisDelivery,
        overbooked: stockOnThisDelivery > stock.quantity_available,
      };
    });
  }, [stocks, deliveryLines]);

  const overbooked = stockDetails.some((d) => d.overbooked);

  const addLocation = (option) => {
    setDeliveryLines((prev) =>
      prev.some((l) => l.destination === option.id)
        ? prev
        : [
            ...prev,
            {
              destination: option.id,
              destinationName: option.name,
              destinationType: option.type,
              articles: [],
            },
          ]
    );
  };

  const removeLine = (destinationId) => {
    setDeliveryLines((prev) =>
      prev.filter((l) => l.destination !== destinationId)
    );
  };

  const addArticleToLine = (destinationId, stock) => {
    setDeliveryLines((prev) =>
      prev.map((line) =>
        line.destination === destinationId &&
        !line.articles.some((a) => a.stock.id === stock.id)
          ? { ...line, articles: [...line.articles, { stock, quantity: 1 }] }
          : line
      )
    );
  };

  const removeArticleFromLine = (destinationId, stockId) => {
    setDeliveryLines((prev) =>
      prev.map((line) =>
        line.destination === destinationId
          ? {
              ...line,
              articles: line.articles.filter((a) => a.stock.id !== stockId),
            }
          : line
      )
    );
  };

  const updateQuantity = (destinationId, stockId, quantity) => {
    setDeliveryLines((prev) =>
      prev.map((line) =>
        line.destination === destinationId
          ? {
              ...line,
              articles: line.articles.map((a) =>
                a.stock.id === stockId ? { ...a, quantity } : a
              ),
            }
          : line
      )
    );
  };

  // Replace every destination's article list with the template (Replace
  // semantics). `items` are [{ stock, quantity }] using the live stock objects,
  // so the existing stock/contingent feedback applies to them unchanged.
  const applyTemplateToAll = (items) => {
    setDeliveryLines((prev) =>
      prev.map((line) => ({
        ...line,
        articles: items.map((it) => ({
          stock: it.stock,
          quantity: it.quantity,
        })),
      }))
    );
  };

  const buildSubmitObj = (overrideContingent = false) => {
    const items = deliveryLines.flatMap((line) =>
      line.articles
        .filter((article) => article.quantity > 0)
        .map((article) => ({
          article_id: article.stock.article,
          planned_quantity: article.quantity,
          destination_id: line.destination,
          order_key: 1,
        }))
    );

    if (items.length === 0) return null;

    return {
      event_id: authState?.currentEvent.id,
      delivery_type: 'OUT',
      items,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      ...(noteComment.trim()
        ? { delivery_note_comment: noteComment.trim() }
        : {}),
      ...(overrideContingent ? { override_contingent: true } : {}),
    };
  };

  const doSubmit = async (overrideContingent = false) => {
    const submitObj = buildSubmitObj(overrideContingent);
    if (!submitObj) {
      toast.error('Lieferung ist leer, alle Mengen sind 0. Bitte prüfen!');
      return;
    }

    try {
      const result = await createDelivery(submitObj).unwrap();
      toast.success('Crew-Lieferung wurde erfolgreich angelegt');
      setContingentError(null);
      navigate(`/crew-delivery/${result.id}`);
    } catch (err) {
      const e = err?.data || err;
      if (e?.code === 'contingent_exceeded') {
        setContingentError(e);
        return;
      }
      apiErrorToast(err, 'Fehler beim Anlegen der Crew-Lieferung');
    }
  };

  const handleSubmit = () => doSubmit(false);

  // Only treat as "no stock" once we have a main storage and the query has
  // finished — otherwise we'd flash the error during the initial load.
  const noStock =
    !hasMainStorage || (!stocksFetching && (stocks?.length ?? 0) === 0);

  if (noStock) {
    return (
      <div>
        <h4>Lieferung anlegen</h4>
        <div className='mt-4 alert alert-danger'>
          <p className='mb-0'>
            Keine Crew-Artikel mit Bestand vorhanden, somit keine Auslieferung
            möglich.
          </p>
        </div>
      </div>
    );
  }

  if (!stocks) {
    return (
      <div>
        <h4>Lieferung anlegen</h4>
        <p className='mt-4 text-muted'>Bestände werden geladen…</p>
      </div>
    );
  }

  return (
    <div>
      <h4>Lieferung anlegen</h4>

      <div className='row mt-3'>
        <div className='col-12 col-lg-8'>
          <DestinationPicker
            crewLocations={crewLocations}
            backstageLocations={backstageLocations}
            addedIds={destinationIds}
            onAdd={addLocation}
          />

          <div className='mt-4'>
            <TemplatePanel
              eventId={authState?.currentEvent?.id}
              stocks={stocks}
              locationCount={deliveryLines.length}
              onApplyToAll={applyTemplateToAll}
            />
          </div>

          <div className='mt-4'>
            {deliveryLines.length === 0 ? (
              <p className='text-muted'>
                Noch keine Lieferstelle. Oben eine Crew- oder
                Backstage-Lieferstelle hinzufügen.
              </p>
            ) : (
              deliveryLines.map((line) => (
                <DeliveryDestinationCard
                  key={line.destination}
                  line={line}
                  stocks={stocks}
                  contingentLookup={contingentLookup}
                  onAddArticle={addArticleToLine}
                  onRemoveArticle={removeArticleFromLine}
                  onChangeQuantity={updateQuantity}
                  onRemove={removeLine}
                />
              ))
            )}
          </div>
        </div>

        <div className='col-12 col-lg-4 mt-4 mt-lg-0'>
          <StockSummaryPanel
            stockDetails={stockDetails}
            overbooked={overbooked}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            comment={comment}
            onCommentChange={setComment}
            noteComment={noteComment}
            onNoteCommentChange={setNoteComment}
          />
        </div>
      </div>

      <ContingentExceededModal
        error={contingentError}
        onClose={() => setContingentError(null)}
        onOverride={() => doSubmit(true)}
      />
    </div>
  );
};

export default CreateCrewDelivery;
